// components/products/sections/SizeVariantSection.jsx
import React, { useState } from "react";
import { Plus, X, Package } from "lucide-react";

const SizeVariantSection = ({
  formData,
  setFormData,
  addSize,
  removeSize,
  errors,
}) => {
  const [newSize, setNewSize] = useState("");
  const [customSizeInput, setCustomSizeInput] = useState("");

  const commonSizes = {
    RING: [
      "5",
      "6",
      "7",
      "8",
      "9",
      "10",
      "11",
      "12",
      "13",
      "14",
      "15",
      "16",
      "17",
      "18",
      "19",
      "20",
      "21",
      "22",
    ],
    BRACELET: ["XS", "S", "M", "L", "XL", "Adjustable"],
    BANGLE: ["2-2", "2-4", "2-6", "2-8", "2-10"],
    NECKLACE: ["14", "16", "18", "20", "22", "24", "Adjustable"],
    CHAIN: ["16", "18", "20", "22", "24", "26", "28", "30"],
    EARRING: ["Standard", "Small", "Medium", "Large"],
    DEFAULT: ["XS", "S", "M", "L", "XL", "XXL", "Free Size"],
  };

  const getSuggestedSizes = () => {
    return commonSizes[formData.subType] || commonSizes.DEFAULT;
  };

  const handleAddSize = () => {
    const sizeToAdd = customSizeInput || newSize;
    if (sizeToAdd) {
      addSize(sizeToAdd);
      setNewSize("");
      setCustomSizeInput("");
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Size & Variant Information
        </h3>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.sizeInfo.hasSize}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                sizeInfo: {
                  ...prev.sizeInfo,
                  hasSize: e.target.checked,
                },
              }))
            }
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-700">Has Multiple Sizes</span>
        </label>
      </div>

      {formData.sizeInfo.hasSize && (
        <>
          {/* Available Sizes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Available Sizes
            </label>

            {formData.sizeInfo.availableSizes.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {formData.sizeInfo.availableSizes.map((size) => (
                  <span
                    key={size}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
                      size === formData.sizeInfo.defaultSize
                        ? "bg-blue-100 text-blue-800 border-2 border-blue-300"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {size}
                    {size === formData.sizeInfo.defaultSize && (
                      <span className="ml-1 text-xs">(Default)</span>
                    )}
                    <button
                      type="button"
                      onClick={() => removeSize(size)}
                      className="ml-2 text-gray-500 hover:text-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Quick Add Suggested Sizes */}
            <div className="space-y-3">
              <div>
                <p className="text-xs text-gray-600 mb-2">Quick add sizes:</p>
                <div className="flex flex-wrap gap-2">
                  {getSuggestedSizes().map((size) => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => addSize(size)}
                      disabled={formData.sizeInfo.availableSizes.includes(size)}
                      className={`px-3 py-1 text-sm rounded-md border ${
                        formData.sizeInfo.availableSizes.includes(size)
                          ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Size Input */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customSizeInput}
                  onChange={(e) => setCustomSizeInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleAddSize()}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter custom size"
                />
                <button
                  type="button"
                  onClick={handleAddSize}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* Default Size Selection */}
          {formData.sizeInfo.availableSizes.length > 0 && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Size
              </label>
              <select
                value={formData.sizeInfo.defaultSize}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    sizeInfo: {
                      ...prev.sizeInfo,
                      defaultSize: e.target.value,
                    },
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select default size</option>
                {formData.sizeInfo.availableSizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Size Chart URL */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Size Chart URL/Reference
            </label>
            <input
              type="text"
              value={formData.sizeInfo.sizeChart}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  sizeInfo: {
                    ...prev.sizeInfo,
                    sizeChart: e.target.value,
                  },
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Link to size chart or sizing guide"
            />
          </div>
        </>
      )}

      {/* Variant Information */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h4 className="font-medium text-gray-900 mb-4">Variant Information</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.isVariant}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    isVariant: e.target.checked,
                  }))
                }
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Is a variant of another product
              </span>
            </label>
          </div>

          {formData.isVariant && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Product ID
                </label>
                <input
                  type="text"
                  value={formData.parentProduct || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      parentProduct: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Parent product ID"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Variant Attribute
                </label>
                <select
                  value={formData.variantAttribute}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      variantAttribute: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select attribute</option>
                  <option value="size">Size</option>
                  <option value="color">Color</option>
                  <option value="metal">Metal Type</option>
                  <option value="purity">Purity</option>
                  <option value="stone">Stone Type</option>
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {errors["sizeInfo.availableSizes"] && (
        <p className="mt-2 text-sm text-red-600">
          {errors["sizeInfo.availableSizes"]}
        </p>
      )}
    </div>
  );
};

export default SizeVariantSection;
